/**
 * Polymarket 实时数据 Hook
 * 根据市场间隔（interval）自动匹配采样颗粒度：
 *   5m / 15m → 1s 颗粒度
 *   1h       → 1m 颗粒度
 *   4h       → 5m 颗粒度
 *   1d       → 30m 颗粒度
 * 同一 bucket 内只更新最后一个数据点，bucket 切换时追加新数据点。
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { RealTimeDataClient } from '@polymarket/real-time-data-client';
import type { Message } from '@polymarket/real-time-data-client';

export interface PriceDataPoint {
  timestamp: number;
  price: number;
  probability: number;
}

export type RealtimePriceSource = 'chainlink' | 'binance';

export interface RealtimePriceData {
  priceData: PriceDataPoint[];
  currentPrice: number;
  baselinePrice: number;
  isConnected: boolean;
  error: string | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastUpdateTime?: number;
  dataSource?: RealtimePriceSource;
}

interface UsePolymarketRealtimeProps {
  token: string; // 如 'btc', 'eth', 'sol'
  interval?: '5m' | '15m' | '1h' | '4h' | '1d'; // 市场时间间隔，决定采样颗粒度
  enabled?: boolean;
  datasource?: RealtimePriceSource; // 数据来源，默认为 chainlink
  eventSlug?: string;
  baselinePrice?: number; // 外部传入的基准价格
}

type PolymarketRealtimeSubscription = {
  topic: string;
  type: string;
  filters: string;
};

function buildRealtimePriceSubscription(
  source: RealtimePriceSource,
  params: { token: string },
): PolymarketRealtimeSubscription {
  const normalizedToken = params.token.toLowerCase();

  if (source === 'chainlink') {
    return {
      topic: 'crypto_prices_chainlink',
      type: '*',
      filters: `{"symbol":"${normalizedToken}/usd"}`,
    };
  }

  return {
    topic: 'crypto_prices',
    type: 'update',
    filters: `{"symbol":"${params.token.toUpperCase()}USDT"}`,
  };
}

function buildRealtimeActivitySubscription(eventSlug: string): PolymarketRealtimeSubscription {
  return {
    topic: 'activity',
    type: 'orders_matched',
    filters: `{"event_slug":"${eventSlug}"}`,
  };
}

/** 根据市场间隔返回 { bucketMs: bucket 大小, bufferSize: 最大缓存点数 } */
function getSamplingConfig(interval?: string): { bucketMs: number; bufferSize: number } {
  switch (interval) {
    case '5m':
      return { bucketMs: 1000, bufferSize: 300 }; // 1s 颗粒，300 点
    case '15m':
      return { bucketMs: 1000, bufferSize: 900 }; // 1s 颗粒，900 点
    case '1h':
      return { bucketMs: 60000, bufferSize: 60 }; // 1m 颗粒，60 点
    case '4h':
      return { bucketMs: 300000, bufferSize: 48 }; // 5m 颗粒，48 点
    case '1d':
      return { bucketMs: 1800000, bufferSize: 48 }; // 30m 颗粒，48 点
    default:
      return { bucketMs: 1000, bufferSize: 900 }; // 默认 1s
  }
}

export const usePolymarketRealtime = ({
  token,
  interval,
  enabled = true,
  eventSlug,
  datasource = 'binance',
  baselinePrice: externalBaselinePrice,
}: UsePolymarketRealtimeProps): RealtimePriceData => {
  const [priceData, setPriceData] = useState<PriceDataPoint[]>([]);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [baselinePrice, setBaselinePrice] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected' | 'error'
  >('disconnected');
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const [dataSource, setDataSource] = useState<RealtimePriceSource>('chainlink');

  const clientRef = useRef<RealTimeDataClient | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5;

  // 价格缓存 refs — onMessage 只写 ref，priceUpdateInterval 统一读 ref 写 state
  const latestPriceRef = useRef<number>(0);
  const latestProbabilityRef = useRef<number>(0.5);
  const latestDataSourceRef = useRef<RealtimePriceSource>('chainlink');
  const latestUpdateTimeRef = useRef<number>(0);
  const priceUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // interval ref，避免 onConnect 闭包过期
  const intervalRef = useRef(interval);
  useEffect(() => {
    intervalRef.current = interval;
  }, [interval]);

  // 清理连接
  const cleanup = useCallback(() => {
    if (clientRef.current) {
      try {
        clientRef.current.disconnect();
      } catch (err) {
        console.warn('Error disconnecting client:', err);
      }
      clientRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (priceUpdateIntervalRef.current) {
      clearInterval(priceUpdateIntervalRef.current);
      priceUpdateIntervalRef.current = null;
    }

    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  // 外部基准价格同步
  useEffect(() => {
    if (externalBaselinePrice && externalBaselinePrice > 0) {
      setBaselinePrice(externalBaselinePrice);
      baselinePriceRef.current = externalBaselinePrice;
    }
  }, [externalBaselinePrice]);

  const baselinePriceRef = useRef(baselinePrice);
  const tokenRef = useRef(token);

  useEffect(() => {
    baselinePriceRef.current = baselinePrice;
    tokenRef.current = token;
  }, [baselinePrice, token]);

  // 处理消息 — 只更新 ref，不触发 re-render
  // currentPrice 的 setState 统一在 priceUpdateInterval 中批量处理
  const onMessage = useCallback((_client: RealTimeDataClient, message: Message): void => {
    try {
      const payload = message.payload as Record<string, unknown> | undefined;
      const timestamp = message.timestamp || Date.now();

      if (!payload) {
        return;
      }

      const isChainlinkMessage = message.topic === 'crypto_prices_chainlink';
      const isBinanceMessage = message.topic === 'crypto_prices' && message.type === 'update';
      const isOrderMatchedMessage =
        message.topic === 'activity' && message.type === 'orders_matched';

      if (isOrderMatchedMessage) {
        const rawTradePrice = payload.price;
        const tradePrice =
          typeof rawTradePrice === 'number' ? rawTradePrice : Number(rawTradePrice);

        if (!Number.isFinite(tradePrice) || tradePrice < 0) {
          return;
        }

        const outcome = String(payload.outcome || '').toLowerCase();
        const outcomeIndex = Number(payload.outcomeIndex);
        const isYesOutcome = outcomeIndex === 0 || outcome === 'yes' || outcome === 'up';
        latestProbabilityRef.current = isYesOutcome ? tradePrice : 1 - tradePrice;
        return;
      }

      if (!isChainlinkMessage && !isBinanceMessage) {
        return;
      }

      const rawValue = payload.value;
      const rawPrice = typeof rawValue === 'number' ? rawValue : Number(rawValue);

      if (!Number.isFinite(rawPrice) || rawPrice <= 0) {
        return;
      }

      // 只更新 ref，不 setState，避免每条消息触发 re-render
      latestPriceRef.current = rawPrice;
      latestDataSourceRef.current = isChainlinkMessage ? 'chainlink' : 'binance';
      latestUpdateTimeRef.current = timestamp;
    } catch (err) {
      console.error('Error processing message:', err);
    }
  }, []);

  // 处理连接成功
  const onConnect = useCallback(
    (client: RealTimeDataClient): void => {
      setIsConnected(true);
      setConnectionStatus('connected');
      setError(null);
      reconnectAttemptsRef.current = 0;

      try {
        const priceSub = buildRealtimePriceSubscription(datasource, { token });
        console.log(
          `[WebSocket] datasource=${datasource}, topic=${priceSub.topic}, type=${priceSub.type}, filters=${priceSub.filters}${eventSlug ? `, activity_eventSlug=${eventSlug}` : ''}`,
        );

        client.subscribe({
          subscriptions: [
            priceSub,
            // ...(eventSlug ? [buildRealtimeActivitySubscription(eventSlug)] : []),
          ],
        });
        if (priceUpdateIntervalRef.current) {
          clearInterval(priceUpdateIntervalRef.current);
        }

        // 每秒检查一次，但数据点按 interval 对应的颗粒度对齐
        // 同时批量更新 currentPrice / lastUpdateTime / dataSource，避免 onMessage 每条消息触发多次 re-render
        priceUpdateIntervalRef.current = setInterval(() => {
          const price = latestPriceRef.current;
          if (price <= 0) return;

          // 批量同步 ref → state（每秒最多 1 次 re-render）
          setCurrentPrice(price);
          setLastUpdateTime(latestUpdateTimeRef.current);
          setDataSource(latestDataSourceRef.current);

          const { bucketMs, bufferSize } = getSamplingConfig(intervalRef.current);
          // 将当前时间对齐到 bucket 边界
          const bucketTimestamp = Math.floor(Date.now() / bucketMs) * bucketMs;

          const newPoint: PriceDataPoint = {
            timestamp: bucketTimestamp,
            price,
            probability: latestProbabilityRef.current,
          };

          setPriceData((prevData) => {
            const last = prevData[prevData.length - 1];
            if (last && last.timestamp === bucketTimestamp) {
              // 同一 bucket：只更新最后一个数据点（不新增）
              if (last.price === price && last.probability === newPoint.probability) {
                // 数据没变，返回原引用，避免无意义的 re-render
                return prevData;
              }
              return [...prevData.slice(0, -1), newPoint];
            }
            // 新 bucket：追加数据点
            const newData = [...prevData, newPoint].slice(-bufferSize);
            return newData;
          });
        }, 1000); // 每 1s 检查，颗粒度由 bucketMs 控制
      } catch (err) {
        console.error('Error subscribing to topics:', err);
        setError('Failed to subscribe to data feeds');
      }
    },
    [datasource, eventSlug, token],
  );

  // 处理连接错误和重连
  const handleConnectionError = useCallback(
    (error: Error) => {
      console.error('Polymarket WebSocket error:', error);
      setError(error.message);
      setConnectionStatus('error');
      setIsConnected(false);

      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);

        reconnectTimeoutRef.current = setTimeout(() => {
          if (!enabled || !token) return;

          cleanup();
          setConnectionStatus('connecting');

          try {
            clientRef.current = new RealTimeDataClient({
              onConnect,
              onMessage,
            });
            clientRef.current.connect();
          } catch (err) {
            console.error('Reconnection failed:', err);
            const retryError = err instanceof Error ? err : new Error('Reconnection failed');
            handleConnectionError(retryError);
          }
        }, delay);
      } else {
        console.error('Max reconnection attempts reached');
        setError('Connection failed after multiple attempts');
      }
    },
    [maxReconnectAttempts, enabled, token, cleanup, onConnect, onMessage],
  );

  // 初始化连接
  const initializeConnection = useCallback(() => {
    if (!enabled || !token) {
      return;
    }

    cleanup();
    setConnectionStatus('connecting');

    try {
      clientRef.current = new RealTimeDataClient({
        onConnect,
        onMessage,
      });
      clientRef.current.connect();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to connect');
      handleConnectionError(error);
    }
  }, [enabled, token, onConnect, onMessage, cleanup, handleConnectionError]);

  useEffect(() => {
    if (enabled && token) {
      initializeConnection();
    } else {
    }
    return cleanup;
  }, [enabled, token, initializeConnection, cleanup]);

  return {
    priceData,
    currentPrice,
    baselinePrice,
    isConnected,
    error,
    connectionStatus,
    lastUpdateTime,
    dataSource,
  };
};
